import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useWebinarT } from '@/hooks/useWebinarT';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { updateSessionLayout } from '@/features/live/liveThunks';
import { updateWorkspace } from '@/features/workspace/workspaceThunks';
import {
  DEFAULT_CAM_LAYOUT,
  camLayoutToReplay,
  fromApiCamLayout,
  isSameCamLayout,
  resolvePendingCamLayout,
  toApiCamLayout,
  type CamLayoutConfig,
} from '@/lib/camLayouts';
import { CamLayoutContext } from './camLayoutContext';

/**
 * Mise en page caméras d'une page live. Le back est la source de vérité : la préférence arrive par
 * la metadata de room (initialisée au défaut du workspace à la création de la room) et un
 * changement de régie est écrit par l'API, qui arbitre entre admin et modérateur. Tant que la room
 * n'a rien dit, on affiche le défaut du workspace connu du navigateur ; un choix réglé avant le
 * direct (l'API répond alors 204 sans rien écrire) est rejoué dès que la room existe.
 *
 * La disposition est un réglage du workspace : un changement de régie écrit aussi son défaut, pour
 * que le back-office, la préparation et les lives suivants portent la même valeur.
 */
export default function CamLayoutProvider({
  workspaceId,
  webinarId,
  sessionId,
  role,
  children,
}: {
  workspaceId: number | undefined;
  webinarId: number | undefined;
  sessionId: number | undefined;
  /** L'intervenant reçoit la disposition sans pouvoir la modifier. */
  role: 'admin' | 'moderator' | 'speaker';
  children: ReactNode;
}) {
  const { t } = useWebinarT('common');
  const dispatch = useAppDispatch();
  const workspaceCamLayout = useAppSelector((state) => state.workspace.currentWorkspace?.camLayout);
  const workspaceName = useAppSelector((state) => state.workspace.currentWorkspace?.name);
  const workspaceTimezone = useAppSelector((state) => state.workspace.currentWorkspace?.timezone);
  const workspacePromoCode = useAppSelector((state) => state.workspace.currentWorkspace?.promoCode);
  const isWorkspaceAdmin = useAppSelector(
    (state) => state.workspace.currentWorkspace?.currentUserRole === 'ADMIN',
  );
  const canEdit = role === 'admin' || role === 'moderator';
  const [roomLayout, setRoomLayout] = useState<CamLayoutConfig | null>(null);
  const [pendingLayout, setPendingLayout] = useState<CamLayoutConfig | null>(null);
  const [effective, setEffectiveState] = useState<CamLayoutConfig>(DEFAULT_CAM_LAYOUT);
  /** Choix accepté par l'API mais pas encore écrit (pas de room) : à rejouer dès qu'elle existe. */
  const layoutToReplayRef = useRef<CamLayoutConfig | null>(null);
  /** Numéro de la dernière écriture lancée : une réponse plus ancienne ne décide plus de rien. */
  const writeSequenceRef = useRef(0);
  /** Écritures encore en vol : au-delà, c'est la room qui dit la vérité. */
  const writesInFlightRef = useRef(0);
  /** Dernier état publié par la room, lisible depuis une réponse d'écriture. */
  const roomLayoutRef = useRef<CamLayoutConfig | null>(null);

  // Identité stable : sans mémoïsation, la valeur du contexte changerait à chaque rendu et ferait
  // re-rendre tous les consommateurs (`useCamLayout`) de la page live.
  const workspaceDefault = useMemo(
    () => fromApiCamLayout(workspaceCamLayout) ?? DEFAULT_CAM_LAYOUT,
    [workspaceCamLayout],
  );
  const preferred = pendingLayout ?? roomLayout ?? workspaceDefault;

  /**
   * Un modérateur extérieur au workspace n'a pas le droit d'écrire ce défaut : son changement ne
   * vaut alors que pour le live en cours, sans rien lui signaler. Le nom et le fuseau sont réécrits
   * à chaque enregistrement, donc tant qu'on ne les connaît pas, on ne touche à rien.
   */
  const saveWorkspaceDefault = useCallback(
    (next: CamLayoutConfig, sequence: number) => {
      if (!isWorkspaceAdmin || workspaceId == null || !workspaceName || !workspaceTimezone) return;
      void dispatch(
        updateWorkspace({
          workspaceId,
          input: {
            name: workspaceName,
            timezone: workspaceTimezone,
            promoCode: workspacePromoCode,
            camLayout: toApiCamLayout(next),
          },
        }),
      ).then((result) => {
        if (sequence !== writeSequenceRef.current) return;
        if (updateWorkspace.rejected.match(result)) toast.error(t('camLayout.updateError'));
      });
    },
    [
      dispatch,
      isWorkspaceAdmin,
      t,
      workspaceId,
      workspaceName,
      workspacePromoCode,
      workspaceTimezone,
    ],
  );

  const pushToApi = useCallback(
    (next: CamLayoutConfig) => {
      if (!canEdit || workspaceId == null || webinarId == null || sessionId == null) return;

      const send = (config: CamLayoutConfig, isReplay: boolean) => {
        const sequence = (writeSequenceRef.current += 1);
        writesInFlightRef.current += 1;
        void dispatch(
          updateSessionLayout({
            workspaceId,
            webinarId,
            sessionId,
            input: { camLayout: toApiCamLayout(config) },
          }),
        ).then((result) => {
          writesInFlightRef.current -= 1;
          // Une réponse dépassée ne doit ni annuler ni confirmer un choix plus récent.
          if (sequence !== writeSequenceRef.current) return;
          if (updateSessionLayout.rejected.match(result)) {
            // Refus (droits, conflit, panne) : on rend la main à ce que la room connaît et on le
            // dit, sinon le réglage paraît avoir sauté tout seul.
            setPendingLayout(null);
            toast.error(t('camLayout.updateError'));
            return;
          }
          // Le défaut du workspace ne part qu'une fois le live servi : en cas de croisement, c'est
          // la dernière intention qui écrit, et un échec de session n'en laisse jamais la trace.
          if (!isReplay) saveWorkspaceDefault(config, sequence);
          if (result.payload.applied) return;
          // 204 : accepté sans rien écrire, faute de room. Si la room est apparue pendant l'envoi,
          // son premier état est déjà passé et aucun autre n'est garanti : on republie tout de
          // suite. Sinon l'intention attend le premier état publié.
          const knownRoom = roomLayoutRef.current;
          if (!isReplay && knownRoom && !isSameCamLayout(knownRoom, config)) {
            send(config, true);
            return;
          }
          layoutToReplayRef.current = config;
        });
      };

      send(next, false);
    },
    [canEdit, dispatch, saveWorkspaceDefault, sessionId, t, webinarId, workspaceId],
  );

  const applyFromRoom = useCallback(
    (config: CamLayoutConfig) => {
      roomLayoutRef.current = config;
      setRoomLayout((previous) =>
        previous && isSameCamLayout(previous, config) ? previous : config,
      );
      const hasWriteInFlight = writesInFlightRef.current > 0;
      setPendingLayout((pending) => resolvePendingCamLayout(pending, config, hasWriteInFlight));

      const toReplay = camLayoutToReplay(layoutToReplayRef.current, config);
      layoutToReplayRef.current = null;
      if (toReplay) {
        setPendingLayout(toReplay);
        pushToApi(toReplay);
      }
    },
    [pushToApi],
  );

  const setPreferred = useCallback(
    (next: CamLayoutConfig) => {
      layoutToReplayRef.current = null;
      setPendingLayout(next);
      pushToApi(next);
    },
    [pushToApi],
  );

  const setEffective = useCallback((next: CamLayoutConfig) => {
    setEffectiveState((previous) => (isSameCamLayout(previous, next) ? previous : next));
  }, []);

  const value = useMemo(
    () => ({ preferred, effective, setPreferred, setEffective, canEdit, applyFromRoom }),
    [preferred, effective, setPreferred, setEffective, canEdit, applyFromRoom],
  );

  return <CamLayoutContext.Provider value={value}>{children}</CamLayoutContext.Provider>;
}
